import React, {
    ChangeEvent,
    Fragment,
    KeyboardEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Transition } from '@headlessui/react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { format, parseISO, isValid } from 'date-fns';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';

interface Props {
    /** Date in `yyyy-MM-dd` format, or '' when unset. Mirrors the
     *  shape of the native `<input type="date">` it replaces. */
    value: string;
    onChange: (next: string) => void;
}

// Committed values are always `yyyy-MM-dd` (10 chars, zero-padded)
// because `stringify` enforces that shape. We use the same length
// to detect "the draft is a complete date" for auto-commit.
// Tied to the ISO format: if the input is ever localised to
// per-locale formats (`MM/dd/yyyy`, `dd.MM.yyyy`, …), this length
// check breaks.
const COMPLETE_DATE_LENGTH = 10;

// Convert the parent's `yyyy-MM-dd` string to a `Date`, returning
// undefined for the empty/invalid case.
const parse = (value: string): Date | undefined => {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
};

const stringify = (d: Date): string => format(d, 'yyyy-MM-dd');

// A draft is "committable" when it's either empty (clears the
// filter) or a complete, valid `yyyy-MM-dd` date. The length check
// is load-bearing — `parseISO` alone accepts partial ISO prefixes
// like `"2026"` / `"2026-05"`, which we don't want to fire as a
// filter change.
const isCommittable = (draft: string): boolean => {
    if (draft === '') return true;
    if (draft.length !== COMPLETE_DATE_LENGTH) return false;
    return isValid(parseISO(draft));
};

/**
 * Date filter field: typeable `yyyy-mm-dd` input + a calendar popup.
 *
 * Open/close state is owned locally rather than via
 * `<PopoverButton>` because we need three things at once:
 *  - clicking *anywhere* in the field opens the popup,
 *  - the popup stays open while the user types in the input,
 *  - clicks outside the field+panel close it.
 *
 * Headless UI's `PopoverButton` couples toggle to button clicks,
 * which conflicts with the input being clickable for cursor
 * placement. Controlled state side-steps that.
 */
const SinceDatePicker = ({ value, onChange }: Props) => {
    const { i18next } = useAppContext();
    const selected = parse(value);

    const [open, setOpen] = useState(false);
    // Local in-progress text. Decoupled from `value` so partial
    // input (`"2026-05"`) doesn't fire a filter change on every
    // keystroke — we only commit when the draft is complete. See
    // `commitDraft` for the commit triggers.
    const [draft, setDraft] = useState(value);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Re-sync the draft when the applied value changes from outside
    // (calendar pick, footer Clear/Today, parent-driven reset).
    useEffect(() => {
        setDraft(value);
    }, [value]);

    // Close when the user clicks anywhere outside the field/panel.
    useEffect(() => {
        if (!open) return;
        const onMouseDown = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open]);

    // Skip `onChange` when the draft already equals the applied
    // value — `updateFilter` upstream allocates a new filter object
    // on every call, which would re-fire the search effect even for
    // a no-op change. Matters here because we have two commit
    // triggers (auto-commit at 10 chars + blur) that can both fire
    // for the same value.
    const commitIfChanged = (next: string) => {
        if (next !== value) onChange(next);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setDraft(next);
        // Auto-commit the moment the draft becomes a complete valid
        // date, or as soon as the field is cleared.
        if (isCommittable(next)) {
            commitIfChanged(next);
        }
        // Typing means the user has committed to keyboard input —
        // dismiss the calendar to get it out of the way.
        setOpen(false);
    };

    // Commit on Enter / blur. If the draft is committable, fire
    // `onChange` (deduped against the applied value); otherwise
    // bounce the field back to the applied value so the displayed
    // text always reflects the active filter.
    const commitDraft = () => {
        if (isCommittable(draft)) {
            commitIfChanged(draft);
        } else {
            setDraft(value);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitDraft();
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={commitDraft}
                // Focusing the input — whether by click, Tab, or the
                // calendar button doing `inputRef.focus()` — opens
                // the popup. Subsequent clicks for cursor placement
                // don't re-fire focus, so the popup stays put.
                onFocus={() => setOpen(true)}
                placeholder="yyyy-mm-dd"
                autoComplete="off"
                className="focus-ring w-full rounded-sm bg-gray-200 py-2 ps-3 pe-10 text-sm text-gray-800 placeholder:text-gray-500 dark:bg-gray-100"
            />
            <button
                type="button"
                onClick={() => {
                    // Toggle on icon click, refocus the input on
                    // open so the user can keep typing or use the
                    // picker — whichever they prefer.
                    setOpen((prev) => {
                        const next = !prev;
                        if (next) {
                            requestAnimationFrame(() =>
                                inputRef.current?.focus(),
                            );
                        }
                        return next;
                    });
                }}
                aria-label={i18next.t('tickets.filter.openCalendar')}
                className="hover:darken-10 focus-ring absolute end-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-gray-600"
            >
                <Icon iconKey="calendar" size="1.25rem" />
            </button>
            <Transition
                as={Fragment}
                show={open}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0 -translate-y-1 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 -translate-y-1 scale-95"
            >
                <div className="shadow-base absolute end-0 top-full z-30 mt-1 w-72 origin-top rounded-lg bg-white p-2 dark:bg-gray-200">
                    <DayPicker
                        mode="single"
                        selected={selected}
                        onSelect={(d) => {
                            onChange(d ? stringify(d) : '');
                            setOpen(false);
                        }}
                        // Initial visible month: the selected date if
                        // any, otherwise today.
                        defaultMonth={selected ?? new Date()}
                        weekStartsOn={1}
                        footer={
                            <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-2 dark:border-gray-300">
                                <button
                                    type="button"
                                    className="hover:darken-5 text-main rounded-[4px] px-2 py-1 text-sm font-medium"
                                    onClick={() => {
                                        onChange('');
                                        setOpen(false);
                                    }}
                                >
                                    {i18next.t('tickets.filter.clear')}
                                </button>
                                <button
                                    type="button"
                                    className="hover:darken-5 text-main rounded-[4px] px-2 py-1 text-sm font-medium"
                                    onClick={() => {
                                        onChange(stringify(new Date()));
                                        setOpen(false);
                                    }}
                                >
                                    {i18next.t('tickets.filter.today')}
                                </button>
                            </div>
                        }
                        classNames={{
                            root: 'text-sm text-gray-800',
                            month_caption:
                                'flex items-center justify-center pb-2 text-sm font-bold',
                            caption_label: 'px-1',
                            nav: 'flex items-center gap-1',
                            button_previous:
                                'hover:darken-5 flex size-7 items-center justify-center rounded-[4px] text-gray-600',
                            button_next:
                                'hover:darken-5 flex size-7 items-center justify-center rounded-[4px] text-gray-600',
                            chevron: 'fill-current',
                            weekdays: 'mb-1',
                            weekday:
                                'w-9 pb-1 text-xs font-medium text-gray-500',
                            day: 'p-0',
                            day_button:
                                'hover:darken-5 flex size-9 items-center justify-center rounded-[4px]',
                            today: 'text-main font-bold',
                            selected:
                                '[&_button]:bg-main [&_button]:text-iconOnBrand [&_button]:hover:darken-10 [&_button]:font-bold',
                            disabled: 'opacity-40',
                        }}
                        aria-label={i18next.t('tickets.filter.since')}
                    />
                </div>
            </Transition>
        </div>
    );
};

export default SinceDatePicker;
